export type Brand<
  TBase,
  TBranding,
  TReservedName extends string = '__type__'
> = TBase & {
  readonly [K in TReservedName]: TBranding;
} & {
  readonly __base__: TBase;
};

type AnyBrand = { readonly __base__: unknown };

type BaseOf<TBrand extends AnyBrand> =
  TBrand extends {
    readonly __base__: infer TBase;
  }
    ? TBase
  : never;

type Brander<TBrand extends AnyBrand> = (
  value: BaseOf<TBrand>
) => TBrand;

function identity<TBrand extends AnyBrand>(
  value: BaseOf<TBrand>
): TBrand {
  return value as TBrand;
}

export function make<TBrand extends AnyBrand>(
  validator?: (value: BaseOf<TBrand>) => void
): Brander<TBrand> {
  if (!validator) {
    return identity;
  }

  return (value: BaseOf<TBrand>): TBrand => {
    validator(value);
    return identity<TBrand>(value);
  };
}

export function maybe<TBrand extends AnyBrand>(
  brander: Brander<TBrand>,
  value: BaseOf<TBrand> | undefined
): TBrand | undefined {
  return value === undefined ? undefined : brander(value);
}
